<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from '@/i18n'
import { useProjectsStore } from '@/stores/projects'
import { useAuthStore } from '@/stores/auth'
import { ElMessage } from 'element-plus'
import ScriptEditor from '@/components/project/ScriptEditor.vue'
import ImageValidator from '@/components/project/ImageValidator.vue'
import VideoValidator from '@/components/project/VideoValidator.vue'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const projectsStore = useProjectsStore()
const authStore = useAuthStore()

const projectId = computed(() => Number(route.params.id))
const project = computed(() => projectsStore.currentProject)
const loading = computed(() => projectsStore.loading)

const showDeleteDialog = ref(false)

const isActive = computed(() => 
  project.value && ['pending', 'processing', 'script_ready', 'images_ready', 'videos_ready'].includes(project.value.status)
)

const canStart = computed(() => 
  project.value?.status === 'draft'
)

const canDownload = computed(() =>
  project.value?.status === 'completed' && project.value?.final_video_url
)

const canDownloadAssets = computed(() =>
  project.value?.status === 'completed' && authStore.isDirector
)

const downloadingAssets = ref(false)

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    draft: 'info',
    pending: 'warning',
    processing: 'warning',
    script_ready: 'primary',
    images_ready: 'primary',
    videos_ready: 'primary',
    completed: 'success',
    failed: 'danger',
    cancelled: 'info'
  }
  return colors[status] || 'info'
}

function getStepStatus(stepNum: number): string {
  if (!project.value) return 'wait'
  
  // For processing status, use current_step from backend
  // For other statuses, map to a specific step
  let currentStep: number
  
  if (project.value.status === 'processing') {
    // Use backend's current_step for processing
    currentStep = project.value.current_step
  } else {
    const statusMap: Record<string, number> = {
      draft: 0,
      pending: 1,
      script_ready: 2,
      images_ready: 3,
      videos_ready: 4,
      completed: 5,
      failed: 0,
      cancelled: 0
    }
    currentStep = statusMap[project.value.status] || 0
  }
  
  if (stepNum < currentStep) return 'finish'
  if (stepNum === currentStep) return 'process'
  return 'wait'
}

async function startProject() {
  if (!project.value) return
  try {
    await projectsStore.startProject(project.value.id)
  } catch (error: any) {
    const message = error.response?.data?.detail || t('common.error')
    ElMessage.error(message)
  }
}

async function cancelProject() {
  if (!project.value) return
  await projectsStore.cancelProject(project.value.id)
}

async function deleteProject() {
  if (!project.value) return
  await projectsStore.deleteProject(project.value.id)
  router.push('/dashboard')
}

function downloadVideo() {
  if (!project.value?.final_video_url) return
  window.open(project.value.final_video_url, '_blank')
}

async function downloadAssets() {
  if (!project.value || !canDownloadAssets.value) return
  
  downloadingAssets.value = true
  try {
    const result = await projectsStore.downloadAssets(project.value.id)
    if (result.download_url) {
      window.open(result.download_url, '_blank')
      ElMessage.success(t('project.download_assets_success'))
    }
  } catch (error: any) {
    if (error.response?.status === 403) {
      ElMessage.error(t('project.director_required'))
    } else {
      ElMessage.error(t('project.download_assets_error'))
    }
  } finally {
    downloadingAssets.value = false
  }
}

// Script editor functions
const showScriptEditor = computed(() => 
  project.value?.status === 'script_ready' && project.value?.mode === 'manual'
)

async function handleUpdateScene(sceneId: number, data: any) {
  if (!project.value) return
  try {
    await projectsStore.updateScene(project.value.id, sceneId, data)
  } catch (error) {
    ElMessage.error(t('script.error_update'))
  }
}

async function handleCreateScene(data: any) {
  if (!project.value) return
  try {
    await projectsStore.createScene(project.value.id, data)
  } catch (error) {
    ElMessage.error(t('script.error_create'))
  }
}

async function handleDeleteScene(sceneId: number) {
  if (!project.value) return
  try {
    await projectsStore.deleteScene(project.value.id, sceneId)
  } catch (error) {
    ElMessage.error(t('script.error_update'))
  }
}

async function handleValidateScript() {
  if (!project.value) return
  try {
    await projectsStore.validateScript(project.value.id)
    ElMessage.success(t('script.scene_updated'))
    // Start polling for progress
    projectsStore.startPolling(project.value.id)
  } catch (error) {
    ElMessage.error(t('script.error_update'))
  }
}

async function refreshProject() {
  if (!project.value) return
  await projectsStore.fetchProject(project.value.id)
}

// Image validation functions (manual mode)
const showImageValidator = computed(() => 
  project.value?.status === 'images_ready' && project.value?.mode === 'manual'
)

const regenerationsRemaining = computed(() => 
  3 - (project.value?.image_regenerations_count || 0)
)

async function handleRegenerateImage(sceneId: number, newPrompt: string) {
  if (!project.value) return
  try {
    await projectsStore.regenerateSceneImageManual(project.value.id, sceneId, newPrompt)
    // Start polling for progress
    projectsStore.startPolling(project.value.id)
  } catch (error) {
    ElMessage.error(t('images.upload_error'))
  }
}

async function handleUploadImage(sceneId: number, file: File) {
  if (!project.value) return
  try {
    await projectsStore.uploadSceneImage(project.value.id, sceneId, file)
  } catch (error) {
    ElMessage.error(t('images.upload_error'))
  }
}

async function handleValidateImages() {
  if (!project.value) return
  try {
    await projectsStore.validateImages(project.value.id)
    ElMessage.success(t('images.validate_and_generate'))
    // Start polling for progress
    projectsStore.startPolling(project.value.id)
  } catch (error) {
    ElMessage.error(t('images.upload_error'))
  }
}

// Video validation functions (manual mode)
const showVideoValidator = computed(() => 
  project.value?.status === 'videos_ready' && project.value?.mode === 'manual'
)

const videoRegenerationsRemaining = computed(() => 
  3 - (project.value?.video_regenerations_count || 0)
)

async function handleRegenerateVideo(sceneId: number, newPrompt: string | null) {
  if (!project.value) return
  try {
    await projectsStore.regenerateSceneVideoManual(project.value.id, sceneId, newPrompt)
    // Start polling for progress
    projectsStore.startPolling(project.value.id)
  } catch (error) {
    ElMessage.error(t('videos.regeneration_limit_reached'))
  }
}

async function handleSwapScenes(sceneAId: number, sceneBId: number) {
  if (!project.value) return
  try {
    await projectsStore.swapScenes(project.value.id, sceneAId, sceneBId)
  } catch (error) {
    ElMessage.error(t('common.error'))
  }
}

async function handleValidateVideos() {
  if (!project.value) return
  try {
    await projectsStore.validateVideos(project.value.id)
    ElMessage.success(t('videos.validate_and_merge'))
    // Start polling for progress
    projectsStore.startPolling(project.value.id)
  } catch (error) {
    ElMessage.error(t('common.error'))
  }
}

onMounted(async () => {
  await projectsStore.fetchProject(projectId.value)
  
  // Start polling if active
  if (isActive.value) {
    projectsStore.startPolling(projectId.value)
  }
})

watch(isActive, (active) => {
  if (active) {
    projectsStore.startPolling(projectId.value)
  } else {
    projectsStore.stopPolling()
  }
})

onUnmounted(() => {
  projectsStore.stopPolling()
})
</script>

<template>
  <div class="project-view">
    <!-- Loading -->
    <div v-if="loading && !project" class="project-loading">
      <el-skeleton :rows="5" animated />
    </div>

    <!-- Project not found -->
    <div v-else-if="!project" class="project-not-found">
      <h2>{{ t('project.not_found') }}</h2>
      <router-link to="/dashboard">{{ t('project.back_to_dashboard') }}</router-link>
    </div>

    <!-- Project content -->
    <template v-else>
      <!-- Header -->
      <div class="project-header">
        <router-link to="/dashboard" class="back-link">
          ← {{ t('common.back') }}
        </router-link>
        
        <div class="header-main">
          <div class="header-info">
            <h1>{{ project.title }}</h1>
            <div class="header-meta">
              <el-tag :type="getStatusColor(project.status)">
                {{ t(`status.${project.status}`) }}
              </el-tag>
              <span class="meta-item">{{ project.format }}</span>
              <span v-if="project.visual_style" class="meta-item">
                {{ project.visual_style }}
              </span>
            </div>
          </div>
          
          <div class="header-actions">
            <el-button 
              v-if="canStart"
              type="primary"
              @click="startProject"
              :loading="loading"
            >
              {{ t('project.start') }}
            </el-button>
            
            <el-button 
              v-if="canDownload"
              type="success"
              @click="downloadVideo"
            >
              {{ t('common.download') }}
            </el-button>
            
            <el-button 
              v-if="isActive"
              type="warning"
              @click="cancelProject"
            >
              {{ t('project.cancel') }}
            </el-button>
            
            <el-button 
              type="danger"
              @click="showDeleteDialog = true"
            >
              {{ t('project.delete') }}
            </el-button>
          </div>
        </div>
      </div>

      <!-- Progress Steps -->
      <div class="project-steps">
        <el-steps :active="project.current_step" finish-status="success" align-center>
          <el-step :title="t('wizard.step1')" :status="getStepStatus(1)" />
          <el-step :title="t('wizard.step2')" :status="getStepStatus(2)" />
          <el-step :title="t('wizard.step3')" :status="getStepStatus(3)" />
          <el-step :title="t('wizard.step4')" :status="getStepStatus(4)" />
          <el-step :title="t('wizard.step5')" :status="getStepStatus(5)" />
        </el-steps>
      </div>

      <!-- Progress Bar for active projects -->
      <div v-if="isActive && project.progress > 0" class="project-progress">
        <el-progress 
          :percentage="project.progress" 
          :stroke-width="12"
          :color="'var(--m2v-primary)'"
        />
        <p class="progress-label">{{ t('project.processing', { progress: project.progress }) }}</p>
      </div>

      <!-- Script Editor (Manual mode - script_ready status) -->
      <div v-if="showScriptEditor" class="script-editor-section">
        <el-alert 
          type="info" 
          :title="t('script.awaiting_validation')"
          show-icon
          :closable="false"
          class="script-info-alert"
        />
        <ScriptEditor
          :project-id="project.id"
          :scenes="project.scenes"
          :project-status="project.status"
          :project-mode="project.mode"
          @validate="handleValidateScript"
          @update-scene="handleUpdateScene"
          @create-scene="handleCreateScene"
          @delete-scene="handleDeleteScene"
          @refresh="refreshProject"
        />
      </div>

      <!-- Image Validator (Manual mode - images_ready status) -->
      <div v-if="showImageValidator" class="image-validator-section">
        <el-alert 
          type="info" 
          :title="t('images.awaiting_validation')"
          show-icon
          :closable="false"
          class="images-info-alert"
        />
        <ImageValidator
          :project-id="project.id"
          :scenes="project.scenes"
          :project-status="project.status"
          :project-mode="project.mode"
          :regenerations-remaining="regenerationsRemaining"
          @validate="handleValidateImages"
          @regenerate="handleRegenerateImage"
          @upload="handleUploadImage"
          @refresh="refreshProject"
        />
      </div>

      <!-- Video Validator (Manual mode - videos_ready status) -->
      <div v-if="showVideoValidator" class="video-validator-section">
        <el-alert 
          type="info" 
          :title="t('videos.awaiting_validation')"
          show-icon
          :closable="false"
          class="videos-info-alert"
        />
        <VideoValidator
          :project-id="project.id"
          :scenes="project.scenes"
          :project-status="project.status"
          :project-mode="project.mode"
          :regenerations-remaining="videoRegenerationsRemaining"
          @validate="handleValidateVideos"
          @regenerate="handleRegenerateVideo"
          @swap="handleSwapScenes"
          @refresh="refreshProject"
        />
      </div>

      <!-- Completed Video -->
      <div v-if="project.status === 'completed' && project.final_video_url" class="project-video">
        <h2>{{ t('project.your_video') }}</h2>
        <div class="video-container">
          <video 
            :src="project.final_video_url" 
            controls 
            class="video-player"
          />
        </div>
        <div class="video-actions">
          <el-button type="primary" @click="downloadVideo">
            {{ t('common.download') }}
          </el-button>
          <el-button 
            v-if="canDownloadAssets"
            type="success" 
            @click="downloadAssets"
            :loading="downloadingAssets"
          >
            <span class="director-badge">👑</span>
            {{ t('project.download_assets') }}
          </el-button>
          <el-tooltip 
            v-else-if="project.status === 'completed'"
            :content="t('project.director_required_desc')"
            placement="top"
          >
            <el-button type="info" disabled>
              <span class="director-badge">👑</span>
              {{ t('project.download_assets') }}
            </el-button>
          </el-tooltip>
          <el-button v-if="project.is_public">
            {{ t('common.share') }}
          </el-button>
        </div>
      </div>

      <!-- Error Message -->
      <el-alert 
        v-if="project.status === 'failed' && project.error_message"
        type="error"
        :title="project.error_message"
        show-icon
        class="project-error"
      />

      <!-- Scenes Preview (hidden during validation modes) -->
      <div v-if="project.scenes.length > 0 && !showImageValidator && !showVideoValidator" class="project-scenes">
        <h2>{{ t('project.scenes_count', { count: project.scenes.length }) }}</h2>
        <div class="scenes-grid">
          <div 
            v-for="scene in project.scenes" 
            :key="scene.id"
            class="scene-card"
          >
            <div class="scene-thumbnail">
              <img v-if="scene.image_url" :src="scene.image_url" :alt="`Scene ${scene.idx + 1}`" />
              <div v-else class="thumbnail-placeholder">
                <span>{{ scene.idx + 1 }}</span>
              </div>
              <div class="scene-duration">
                {{ scene.duration_s.toFixed(1) }}s
              </div>
            </div>
            <div class="scene-info">
              <div class="scene-header">
                <span class="scene-number">{{ t('project.scene_number', { number: scene.idx + 1 }) }}</span>
                <el-tag size="small" :type="scene.status === 'video_ready' ? 'success' : 'info'">
                  {{ t(`status.${scene.status}`) || scene.status }}
                </el-tag>
              </div>
              <p v-if="scene.description" class="scene-description">
                {{ scene.description }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Credits Info -->
      <div class="project-credits">
        <div class="credits-card">
          <span class="credits-icon">💎</span>
          <div class="credits-details">
            <span v-if="project.credits_reserved > 0">
              Crédits réservés: {{ project.credits_reserved }}
            </span>
            <span v-if="project.credits_used > 0">
              Crédits utilisés: {{ project.credits_used }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <!-- Delete Dialog -->
    <el-dialog
      v-model="showDeleteDialog"
      :title="t('project.confirm_delete_title')"
      width="400px"
    >
      <p>{{ t('project.confirm_delete_message') }}</p>
      <template #footer>
        <el-button @click="showDeleteDialog = false">{{ t('common.cancel') }}</el-button>
        <el-button type="danger" @click="deleteProject">{{ t('common.delete') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
.project-view {
  min-height: calc(100vh - 64px);
  padding: var(--m2v-space-8) var(--m2v-space-6);
  max-width: 1200px;
  margin: 0 auto;
}

.project-loading,
.project-not-found {
  padding: var(--m2v-space-12);
  text-align: center;
}

.project-header {
  margin-bottom: var(--m2v-space-8);
}

.back-link {
  display: inline-block;
  color: var(--m2v-text-muted);
  margin-bottom: var(--m2v-space-4);
  transition: color var(--m2v-transition-fast);
}

.back-link:hover {
  color: var(--m2v-primary-light);
}

.header-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--m2v-space-6);
  flex-wrap: wrap;
}

.header-info h1 {
  font-family: var(--m2v-font-display);
  font-size: 2rem;
  margin-bottom: var(--m2v-space-3);
}

.header-meta {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-3);
  flex-wrap: wrap;
}

.meta-item {
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
}

.header-actions {
  display: flex;
  gap: var(--m2v-space-3);
  flex-wrap: wrap;
}

.project-steps {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-6);
  margin-bottom: var(--m2v-space-6);
}

.project-progress {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-6);
  margin-bottom: var(--m2v-space-6);
}

.progress-label {
  text-align: center;
  color: var(--m2v-text-muted);
  margin-top: var(--m2v-space-3);
}

.project-video {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-6);
  margin-bottom: var(--m2v-space-6);
}

.project-video h2 {
  margin-bottom: var(--m2v-space-4);
}

.video-container {
  aspect-ratio: 16/9;
  background: black;
  border-radius: var(--m2v-radius-lg);
  overflow: hidden;
  margin-bottom: var(--m2v-space-4);
}

.video-player {
  width: 100%;
  height: 100%;
}

.video-actions {
  display: flex;
  gap: var(--m2v-space-3);
  justify-content: center;
  flex-wrap: wrap;
}

.director-badge {
  margin-right: var(--m2v-space-1);
}

.project-error {
  margin-bottom: var(--m2v-space-6);
}

.project-scenes {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-6);
  margin-bottom: var(--m2v-space-6);
}

.project-scenes h2 {
  margin-bottom: var(--m2v-space-4);
}

.scenes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--m2v-space-4);
}

.scene-card {
  background: var(--m2v-bg-elevated);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  overflow: hidden;
}

.scene-thumbnail {
  position: relative;
  aspect-ratio: 16/9;
  background: var(--m2v-bg-base);
}

.scene-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--m2v-text-muted);
}

.scene-duration {
  position: absolute;
  bottom: var(--m2v-space-1);
  right: var(--m2v-space-1);
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: var(--m2v-radius-sm);
  font-size: 0.75rem;
  color: white;
}

.scene-info {
  padding: var(--m2v-space-3);
}

.scene-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--m2v-space-2);
}

.scene-number {
  font-weight: 600;
  font-size: 0.875rem;
}

.scene-description {
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.project-credits {
  margin-bottom: var(--m2v-space-6);
}

.credits-card {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-3);
  padding: var(--m2v-space-4);
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
}

.credits-icon {
  font-size: 1.5rem;
}

.credits-details {
  display: flex;
  gap: var(--m2v-space-4);
  color: var(--m2v-text-muted);
}

.script-editor-section {
  margin-bottom: var(--m2v-space-6);
}

.script-info-alert {
  margin-bottom: var(--m2v-space-4);
}

.image-validator-section {
  margin-bottom: var(--m2v-space-6);
}

.images-info-alert {
  margin-bottom: var(--m2v-space-4);
}

.video-validator-section {
  margin-bottom: var(--m2v-space-6);
}

.videos-info-alert {
  margin-bottom: var(--m2v-space-4);
}
</style>
